/* ============================================================================
 * GESKE - Gift with purchase engine
 * ----------------------------------------------------------------------------
 * Reads the rules the GESKE app publishes to the shop metafield `gwp.config`
 * (rendered by snippets/gwp-config.liquid into #gwp-engine-config), works out
 * which gifts the current cart has earned, and adds/removes/swaps the gift
 * lines through the Ajax Cart API. Gift lines are marked with the line
 * property `_gwp: <rule handle>`.
 *
 * Pricing is NOT this file's job: every rule is backed by a native Shopify
 * automatic discount (created by the app) that zeroes the gift line in the
 * cart and at checkout. The engine is convenience + UX. Its evaluation is
 * deliberately at least as strict as Shopify's discount (spend counted after
 * discounts, gift lines excluded), so it never adds a gift Shopify won't make
 * free. If that still happens (a competing BXGY on the same items, a code that
 * can't combine) the gift is removed again, the rule is remembered as `stalled`
 * for that paid cart (no re-add loop), and only an un-removable charged gift
 * makes geske-cart.js show the checkout guard.
 *
 * Public API (window.GeskeGwp):
 *   config()                 merged config or null when no rules are published
 *   isRuleHandle(value)      true when `value` is a published rule handle
 *   evaluate(cart)           pure: cart -> evaluation (see evaluate())
 *   reconcile(cart?)         apply desired state; resolves {mutated, cart, ev}
 *   dismiss(handle)          customer removed a gift: remember + remove
 *   choose(handle, variantId) CHOOSE mode selection / swap
 *   needsChoice(ev)          CHOOSE rules that are unlocked, unchosen and pickable
 *   chooser.open(opts)       gift chooser modal ({handles?, context:'browse'|'checkout'})
 *   chooser.close()
 *   enqueue(task)            shared serialised mutation queue
 *   templates.group(rule)    drawer HTML for one rule's gift block
 *   templates.tiles(rule)    drawer HTML for CHOOSE tiles
 *   formatMoney(pence)
 *   state()                  last evaluation
 *
 * Events: `geske:gwp` (detail: {cart, ev}) after every reconcile that mutated
 * the cart, plus a plain `cart:update` CustomEvent (source 'gwp-engine') so
 * Horizon components (cart page, cart icon) refresh themselves.
 * ========================================================================== */
(function () {
  'use strict';

  var CONFIG_ID = 'gwp-engine-config';
  var STORAGE = {
    dismissed: 'geske-gwp-dismissed',
    choice: 'geske-gwp-choice',
    oos: 'geske-gwp-oos',
    rate: 'geske-gwp-rate',
    stalled: 'geske-gwp-stalled'
  };
  var RATE_LIMIT = 10;          // mutations per window
  var RATE_WINDOW = 60 * 1000;  // ms
  var OOS_TTL = 10 * 60 * 1000; // remember sold-out variants for 10 minutes

  var state = {
    loaded: false,
    config: null,
    rules: [],
    byHandle: {},
    giftVariantIds: {},
    moneyFormat: '',
    now: null,
    lastEval: null,
    last: { sig: null, noop: false },
    inFlight: null,
    engineRemovals: {}
  };

  /* --------------------------------- utils -------------------------------- */

  var opQueue = Promise.resolve();
  function enqueue(task) {
    var run = opQueue.then(task, task);
    opQueue = run.catch(function () {});
    return run;
  }

  function readJson(key, fallback) {
    try {
      var raw = window.sessionStorage.getItem(key);
      return raw ? JSON.parse(raw) : fallback;
    } catch (error) {
      return fallback;
    }
  }

  function writeJson(key, value) {
    try {
      window.sessionStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      /* storage blocked: degrade to in-memory */
    }
  }

  function escapeHtml(value) {
    if (value == null) return '';
    return String(value).replace(/[&<>"']/g, function (character) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[character];
    });
  }

  function fillTemplate(template, values) {
    return String(template || '').replace(/\{(\w+)\}/g, function (match, key) {
      return values && Object.prototype.hasOwnProperty.call(values, key) ? values[key] : match;
    });
  }

  function shopCurrency() {
    return (state.config && state.config.currency) ||
      (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'GBP';
  }

  function formatMoney(pence) {
    pence = Number(pence) || 0;
    var format = state.moneyFormat;
    var value = (pence / 100).toFixed(2);
    if (format && format.indexOf('{{amount_no_decimals}}') !== -1) {
      return format.replace('{{amount_no_decimals}}', Math.round(pence / 100).toString());
    }
    if (format && format.indexOf('{{amount}}') !== -1) {
      return format.replace('{{amount}}', value);
    }
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: shopCurrency() }).format(pence / 100);
    } catch (error) {
      return value + ' ' + shopCurrency();
    }
  }

  function formatMoneyShort(pence) {
    return formatMoney(pence).replace(/([.,]00)(?!\d)/, '');
  }

  function thumbUrl(src, size) {
    if (!src) return '';
    return src.replace(/(\.[^.?]+)(\?.*)?$/, '_' + size + 'x$1$2');
  }

  function nowMs() {
    return Date.now();
  }

  /* -------------------------------- config -------------------------------- */

  function loadConfig() {
    if (state.loaded) return state.config;
    state.loaded = true;

    var el = document.getElementById(CONFIG_ID);
    if (!el) return null;

    var raw;
    try {
      raw = JSON.parse(el.textContent || '{}');
    } catch (error) {
      return null;
    }
    var config = raw && raw.config;
    var retired = (config && config.retired) || [];
    if (!config || ((!config.rules || !config.rules.length) && !retired.length)) return null;
    config.rules = config.rules || [];

    var live = raw.live || {};
    var liveVariants = live.variants || {};
    var liveCollections = live.collections || {};
    state.moneyFormat = raw.moneyFormat || '';

    var rules = config.rules.map(function (rule) {
      var variants = (rule.gift.variants || []).map(function (variant) {
        var fresh = liveVariants[String(variant.id)] || {};
        return {
          id: Number(variant.id),
          productId: Number(variant.productId),
          handle: variant.handle,
          title: variant.title,
          variantTitle: variant.variantTitle || null,
          price: typeof fresh.price === 'number' ? fresh.price : Number(variant.price) || 0,
          image: fresh.image || variant.image || null,
          url: fresh.url || (variant.handle ? '/products/' + variant.handle + '?variant=' + variant.id : ''),
          available: fresh.available === undefined ? true : !!fresh.available
        };
      });

      var collectionIds = {};
      (rule.trigger.collections || []).forEach(function (collection) {
        (collection.productIds || []).forEach(function (id) { collectionIds[Number(id)] = true; });
        var fresh = collection.handle ? liveCollections[collection.handle] : null;
        (fresh || []).forEach(function (id) { collectionIds[Number(id)] = true; });
      });
      var productIds = {};
      (rule.trigger.productIds || []).forEach(function (id) { productIds[Number(id)] = true; });

      return {
        handle: rule.handle,
        title: rule.title,
        priority: Number(rule.priority) || 0,
        tierGroup: rule.tierGroup || null,
        tierMode: rule.tierMode || 'STACK',
        trigger: {
          type: rule.trigger.type,
          scope: rule.trigger.scope || 'CART',
          amount: rule.trigger.amount == null ? null : Number(rule.trigger.amount),
          quantity: rule.trigger.quantity == null ? null : Number(rule.trigger.quantity),
          basis: rule.trigger.basis || 'AFTER_DISCOUNTS',
          productIds: productIds,
          collectionProductIds: collectionIds
        },
        gift: {
          mode: rule.gift.mode || 'AUTO',
          quantity: Math.max(1, Number(rule.gift.quantity) || 1),
          allowRemove: rule.gift.allowRemove !== false,
          variants: variants
        },
        schedule: rule.schedule || {},
        messages: rule.messages || {},
        discountId: rule.discountId || null
      };
    });

    rules.sort(function (a, b) { return b.priority - a.priority; });

    state.rules = rules;
    state.byHandle = {};
    state.retired = {};
    retired.forEach(function (h) { state.retired[String(h)] = true; });
    state.giftVariantIds = {};
    rules.forEach(function (rule) {
      state.byHandle[rule.handle] = rule;
      rule.gift.variants.forEach(function (variant) { state.giftVariantIds[variant.id] = true; });
    });

    state.config = {
      version: config.version,
      currency: config.currency,
      settings: config.settings || {},
      rules: rules,
      retired: retired,
      moneyFormat: state.moneyFormat,
      hideGiftTag: (config.settings && config.settings.hideGiftTag) || 'gwp-gift'
    };
    return state.config;
  }

  function config() {
    return loadConfig();
  }

  function isRuleHandle(value) {
    loadConfig();
    return !!(value && state.byHandle[String(value)]);
  }

  /* ----------------------------- session memory --------------------------- */

  // Dismissals live in memory for the page and (by default) in sessionStorage
  // for the tab. With rememberDismissal off they still hold for the page, or
  // the engine would re-add the gift the moment the customer removed it.
  var memDismissed = {};
  function rememberDismissals() {
    var settings = (state.config && state.config.settings) || {};
    return settings.rememberDismissal !== false;
  }
  function dismissed() {
    var stored = rememberDismissals() ? readJson(STORAGE.dismissed, {}) : {};
    var merged = {};
    Object.keys(stored).forEach(function (k) { merged[k] = true; });
    Object.keys(memDismissed).forEach(function (k) { merged[k] = true; });
    return merged;
  }
  function setDismissed(map) {
    memDismissed = {};
    Object.keys(map).forEach(function (k) { memDismissed[k] = true; });
    if (rememberDismissals()) writeJson(STORAGE.dismissed, map);
  }
  function choices() { return readJson(STORAGE.choice, {}); }
  function setChoices(map) { writeJson(STORAGE.choice, map); }

  function oosMap() {
    var map = readJson(STORAGE.oos, {});
    var cutoff = nowMs() - OOS_TTL;
    var clean = {};
    Object.keys(map).forEach(function (id) { if (map[id] > cutoff) clean[id] = map[id]; });
    return clean;
  }
  function markOos(variantId) {
    var map = oosMap();
    map[String(variantId)] = nowMs();
    writeJson(STORAGE.oos, map);
  }

  // A gift Shopify wouldn't zero (competing BXGY on the same items, a code
  // that doesn't combine) is removed and remembered against a signature of
  // the paid part of the cart + applied codes, so it is retried only when
  // that changes — never a re-add loop, never a charged "free" gift.
  function stalledMemo() { return readJson(STORAGE.stalled, {}); }
  function setStalledMemo(map) { writeJson(STORAGE.stalled, map); }
  function paidSignature(cart, paidLines) {
    var lines = paidLines.map(function (item) {
      return item.variant_id + 'x' + item.quantity + '@' + (item.final_line_price || 0);
    }).sort();
    var codes = ((cart && cart.discount_codes) || []).map(function (c) { return c && c.code; }).filter(Boolean).sort();
    return lines.join(',') + '|' + codes.join(',');
  }

  function rateAllows() {
    var stamps = readJson(STORAGE.rate, []).filter(function (t) { return t > nowMs() - RATE_WINDOW; });
    return stamps.length < RATE_LIMIT;
  }
  function rateStamp() {
    var stamps = readJson(STORAGE.rate, []).filter(function (t) { return t > nowMs() - RATE_WINDOW; });
    stamps.push(nowMs());
    writeJson(STORAGE.rate, stamps);
  }

  /* -------------------------------- evaluate ------------------------------ */

  function cartSig(cart) {
    if (!cart) return 'none';
    return String(cart.token || '') + ':' + (cart.item_count || 0) + ':' + (cart.total_price || 0);
  }

  function ruleActive(rule) {
    var now = nowMs();
    var starts = rule.schedule.startsAt ? Date.parse(rule.schedule.startsAt) : null;
    var ends = rule.schedule.endsAt ? Date.parse(rule.schedule.endsAt) : null;
    if (starts && !isNaN(starts) && now < starts) return false;
    if (ends && !isNaN(ends) && now > ends) return false;
    return true;
  }

  function lineMatchesScope(rule, item) {
    var trigger = rule.trigger;
    if (trigger.type === 'PRODUCT_QTY') return !!trigger.productIds[item.product_id];
    if (trigger.type === 'COLLECTION_QTY') return !!trigger.collectionProductIds[item.product_id];
    // SPEND
    if (trigger.scope === 'PRODUCTS') return !!trigger.productIds[item.product_id];
    if (trigger.scope === 'COLLECTIONS') return !!trigger.collectionProductIds[item.product_id];
    return true;
  }

  function lineAmount(rule, item) {
    if (rule.trigger.basis === 'BEFORE_DISCOUNTS') return item.original_line_price || 0;
    return item.final_line_price != null ? item.final_line_price : (item.line_price || 0);
  }

  function qualifies(rule, paidLines) {
    var matched = paidLines.filter(function (item) { return lineMatchesScope(rule, item); });
    if (rule.trigger.type === 'SPEND') {
      var spend = matched.reduce(function (sum, item) { return sum + lineAmount(rule, item); }, 0);
      var amount = rule.trigger.amount || 0;
      return {
        matchedQty: matched.reduce(function (sum, item) { return sum + (item.quantity || 0); }, 0),
        spend: spend,
        target: amount,
        remaining: Math.max(0, amount - spend),
        progress: amount > 0 ? Math.max(0, Math.min(100, (spend / amount) * 100)) : 100,
        qualifies: matched.length > 0 && amount > 0 && spend >= amount
      };
    }
    var qty = matched.reduce(function (sum, item) { return sum + (item.quantity || 0); }, 0);
    var need = rule.trigger.quantity || 1;
    return {
      matchedQty: qty,
      spend: 0,
      target: need,
      remaining: Math.max(0, need - qty),
      progress: need > 0 ? Math.max(0, Math.min(100, (qty / need) * 100)) : 100,
      qualifies: qty >= need
    };
  }

  function variantById(rule, id) {
    id = Number(id);
    for (var i = 0; i < rule.gift.variants.length; i++) {
      if (rule.gift.variants[i].id === id) return rule.gift.variants[i];
    }
    return null;
  }

  function firstAvailableVariant(rule, oos) {
    for (var i = 0; i < rule.gift.variants.length; i++) {
      var variant = rule.gift.variants[i];
      if (variant.available && !oos[String(variant.id)]) return variant;
    }
    return null;
  }

  function evaluate(cart) {
    var cfg = loadConfig();
    var items = (cart && cart.items) || [];
    var managed = [];
    var legacy = [];
    var paid = [];
    var configuredGiftLines = [];
    var retiredLines = [];

    items.forEach(function (item) {
      var value = item.properties && item.properties._gwp;
      if (value && cfg && state.byHandle[String(value)]) {
        managed.push(item);
      } else if (value && cfg && state.retired && state.retired[String(value)]) {
        // A rule that has since been paused/archived: its gift is no longer
        // free, so take it back out rather than sell it.
        retiredLines.push(item);
      } else if (value) {
        legacy.push(item);
      } else if (cfg && state.giftVariantIds[item.variant_id]) {
        // A gift product bought normally: not our line, but it must not pay
        // for its own unlock (Monpure #21411).
        configuredGiftLines.push(item);
      } else {
        paid.push(item);
      }
    });

    var ev = {
      sig: cartSig(cart),
      cart: cart,
      hasConfig: !!cfg,
      rules: [],
      managedLines: managed,
      retiredLines: retiredLines,
      legacyLines: legacy,
      paidLines: paid,
      configuredGiftLines: configuredGiftLines,
      next: null,
      unlockedTop: null,
      diff: { remove: {}, add: [] },
      guard: { needed: false, handles: [] },
      display: { count: 0, total: 0 },
      giftCharge: 0
    };

    if (!cfg) {
      ev.display.count = cart ? cart.item_count : 0;
      ev.display.total = cart ? cart.total_price : 0;
      return ev;
    }

    retiredLines.forEach(function (item) { ev.diff.remove[item.key] = 0; });

    var dismissedMap = dismissed();
    var choiceMap = choices();
    var oos = oosMap();
    var dismissedChanged = false;
    var stalledMap = stalledMemo();
    var stalledChanged = false;
    var paidSig = paidSignature(cart, paid);

    // Pass 1: qualification.
    var evals = cfg.rules.map(function (rule) {
      var active = ruleActive(rule);
      var q = qualifies(rule, paid);
      var present = managed.filter(function (item) { return String(item.properties._gwp) === rule.handle; });
      return {
        rule: rule,
        handle: rule.handle,
        title: rule.title,
        tierGroup: rule.tierGroup,
        tierMode: rule.tierMode,
        mode: rule.gift.mode,
        active: active,
        qualifies: active && q.qualifies,
        matchedQty: q.matchedQty,
        spend: q.spend,
        target: q.target,
        remaining: q.remaining,
        progress: q.progress,
        winner: false,
        superseded: false,
        dismissed: false,
        stalled: false,
        chosenVariantId: null,
        present: present,
        desired: [],
        status: 'LOCKED',
        values: {},
        messages: {}
      };
    });

    // Pass 2: tier groups (HIGHEST_ONLY keeps only the best unlocked rule).
    var groups = {};
    evals.forEach(function (r) {
      if (!r.tierGroup) return;
      groups[r.tierGroup] = groups[r.tierGroup] || [];
      groups[r.tierGroup].push(r);
    });
    Object.keys(groups).forEach(function (name) {
      var members = groups[name];
      var exclusive = members.some(function (r) { return r.tierMode === 'HIGHEST_ONLY'; });
      if (!exclusive) return;
      var qualified = members.filter(function (r) { return r.qualifies; });
      if (!qualified.length) return;
      qualified.sort(function (a, b) {
        if (b.target !== a.target) return b.target - a.target;
        return b.rule.priority - a.rule.priority;
      });
      qualified.forEach(function (r, index) { if (index > 0) r.superseded = true; });
    });

    // Pass 3: desired state per rule.
    evals.forEach(function (r) {
      var rule = r.rule;
      r.winner = r.qualifies && !r.superseded;
      r.dismissed = !!dismissedMap[rule.handle];

      // Dropping below the threshold forgives an earlier dismissal, so
      // re-crossing it re-adds the gift.
      if (!r.qualifies && r.dismissed) {
        delete dismissedMap[rule.handle];
        r.dismissed = false;
        dismissedChanged = true;
      }

      var presentVariant = r.present.length ? variantById(rule, r.present[0].variant_id) : null;

      // Remembered stall for this exact paid cart: don't offer the gift again
      // until something about the paid lines / codes changes.
      if (stalledMap[rule.handle] && stalledMap[rule.handle] !== paidSig) {
        delete stalledMap[rule.handle];
        stalledChanged = true;
      }
      var stalledMemoHit = !!stalledMap[rule.handle];

      if (r.winner && !r.dismissed && !stalledMemoHit) {
        var target = null;
        if (rule.gift.mode === 'CHOOSE') {
          var chosen = presentVariant || variantById(rule, choiceMap[rule.handle]);
          if (chosen && (chosen.available || presentVariant) && !oos[String(chosen.id)]) target = chosen;
          r.chosenVariantId = target ? target.id : null;
        } else {
          target = presentVariant && !oos[String(presentVariant.id)] ? presentVariant : firstAvailableVariant(rule, oos);
        }
        if (target) r.desired = [{ variantId: target.id, quantity: rule.gift.quantity, variant: target }];
      }

      // Stalled: we think it qualifies, the line is there, Shopify still
      // charges for it (a competing BXGY on the same qualifying items, a code
      // that doesn't combine, a product discount targeting the gift). Remove
      // the gift rather than sell it, and remember not to re-add it for this
      // paid cart. Only if removal is impossible does the checkout guard fire.
      if (stalledMemoHit) {
        r.stalled = true;
        r.desired = [];
      } else if (r.winner && r.present.length) {
        var charged = r.present.some(function (item) { return (item.final_line_price || 0) > 0; });
        if (charged) {
          r.stalled = true;
          r.desired = [];
          stalledMap[rule.handle] = paidSig;
          stalledChanged = true;
        }
      }

      // Diff.
      var keepKeys = {};
      r.desired.forEach(function (want) {
        var matches = r.present.filter(function (item) { return item.variant_id === want.variantId; });
        if (!matches.length) {
          if (!r.stalled) ev.diff.add.push({ id: want.variantId, quantity: want.quantity, properties: { _gwp: rule.handle } });
          return;
        }
        keepKeys[matches[0].key] = true;
        if (matches[0].quantity !== want.quantity && !r.stalled) ev.diff.remove[matches[0].key] = want.quantity;
        for (var i = 1; i < matches.length; i++) ev.diff.remove[matches[i].key] = 0; // duplicates
      });
      r.present.forEach(function (item) {
        if (!keepKeys[item.key] && ev.diff.remove[item.key] === undefined) ev.diff.remove[item.key] = 0;
      });

      // Status + display values.
      var giftVariant = (r.desired[0] && r.desired[0].variant) || presentVariant || rule.gift.variants[0] || null;
      var giftValue = giftVariant ? giftVariant.price * rule.gift.quantity : 0;
      r.values = {
        amount: formatMoneyShort(r.remaining),
        remaining: r.remaining,
        threshold: rule.trigger.type === 'SPEND' ? formatMoneyShort(rule.trigger.amount || 0) : String(rule.trigger.quantity || 1),
        value: formatMoneyShort(giftValue),
        gift: giftVariant ? giftVariant.title : rule.title,
        count: String(rule.gift.quantity)
      };
      var settingsMessages = (cfg.settings && cfg.settings.messages) || {};
      var msg = {
        pending: rule.messages.pending || settingsMessages.pending || 'Add {amount} more to unlock your free {gift}',
        unlocked: rule.messages.unlocked || settingsMessages.unlocked || 'Your free {gift} is in your bag',
        badge: rule.messages.badge || settingsMessages.badge || 'Free gift',
        chooserHeading: rule.messages.chooserHeading || settingsMessages.chooserHeading || 'Choose your free gift'
      };
      r.messages = {
        pending: fillTemplate(msg.pending, r.values),
        unlocked: fillTemplate(msg.unlocked, r.values),
        badge: fillTemplate(msg.badge, r.values),
        chooserHeading: fillTemplate(msg.chooserHeading, r.values)
      };

      if (!r.active) r.status = 'INACTIVE';
      else if (!r.qualifies) r.status = 'LOCKED';
      else if (r.superseded) r.status = 'SUPERSEDED';
      else if (r.dismissed) r.status = 'DISMISSED';
      else if (r.stalled) r.status = r.present.length ? 'STALLED' : 'UNAVAILABLE';
      else if (r.present.length && r.desired.length) r.status = 'UNLOCKED_PRESENT';
      else if (r.desired.length) r.status = 'UNLOCKED_ADD';
      else if (rule.gift.mode === 'CHOOSE') r.status = 'UNLOCKED_CHOOSE';
      else r.status = 'UNLOCKED_UNAVAILABLE';
    });

    if (dismissedChanged) setDismissed(dismissedMap);
    if (stalledChanged) setStalledMemo(stalledMap);

    // Guard: only when a charged managed gift is still in the cart after the
    // diff (i.e. we could not or did not remove it).
    evals.forEach(function (r) {
      if (!r.stalled) return;
      var stillThere = r.present.some(function (item) { return ev.diff.remove[item.key] === undefined; });
      if (stillThere) {
        ev.guard.needed = true;
        ev.guard.handles.push(r.handle);
      }
    });

    ev.rules = evals;

    // "next": the closest unmet spend rule drives the progress bar.
    var candidates = evals.filter(function (r) {
      return r.active && !r.qualifies && r.rule.trigger.type === 'SPEND' && !r.dismissed;
    });
    candidates.sort(function (a, b) {
      if (a.remaining !== b.remaining) return a.remaining - b.remaining;
      return b.rule.priority - a.rule.priority;
    });
    ev.next = candidates[0] || null;

    // Top unlocked rule for messaging: has its gift in the bag, or is waiting
    // for a CHOOSE pick. Stalled/dismissed rules never speak.
    var unlocked = evals.filter(function (r) {
      return r.winner && !r.dismissed && !r.stalled && (r.present.length || r.status === 'UNLOCKED_CHOOSE');
    });
    ev.unlockedTop = unlocked[0] || null;

    // Display: gifts count once each; charged gift lines are hidden from the
    // subtotal because either Shopify zeroes them or the guard removes them.
    var giftCharge = managed.reduce(function (sum, item) { return sum + (item.final_line_price || 0); }, 0);
    ev.giftCharge = giftCharge;
    ev.display.count = cart ? cart.item_count : 0;
    ev.display.total = cart ? Math.max(0, (cart.total_price || 0) - giftCharge) : 0;

    state.lastEval = ev;
    return ev;
  }

  function diffEmpty(diff) {
    return !diff.add.length && !Object.keys(diff.remove).length;
  }

  // Shop-level UI labels published by the app (settings.messages); the
  // fallbacks keep the modal working against a manifest from an older app.
  function uiMessage(key, fallback) {
    var settings = (state.config && state.config.settings) || {};
    var messages = settings.messages || {};
    return messages[key] || fallback;
  }

  // CHOOSE rules that should interrupt checkout: unlocked, nothing picked yet,
  // and at least one variant the customer could actually take. A rule whose
  // gifts are all sold out (or stalled/dismissed) must never block checkout.
  function needsChoice(ev) {
    if (!ev || !ev.rules) return [];
    var oos = oosMap();
    return ev.rules.filter(function (r) {
      if (r.status !== 'UNLOCKED_CHOOSE') return false;
      return r.rule.gift.variants.some(function (variant) {
        return variant.available && !oos[String(variant.id)];
      });
    });
  }

  /* -------------------------------- mutate -------------------------------- */

  function fetchCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (response) { return response.json(); });
  }

  function postJson(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body)
    }).then(function (response) {
      if (!response.ok) {
        return response.json().catch(function () { return {}; }).then(function (error) {
          error = error || {};
          error.status = response.status;
          throw error;
        });
      }
      return response.json();
    });
  }

  function dispatchUpdated(cart, ev) {
    try {
      document.dispatchEvent(new CustomEvent('geske:gwp', { bubbles: false, detail: { cart: cart, ev: ev } }));
    } catch (error) { /* no-op */ }
    try {
      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cart,
          sourceId: 'gwp-engine',
          data: { source: 'gwp-engine', itemCount: cart ? cart.item_count : 0 }
        }
      }));
    } catch (error) { /* no-op */ }
  }

  function addWithFallback(items, cart) {
    // One request for everything; on 422 fall back per item so a sold-out
    // gift doesn't block the others, and AUTO rules can try their next variant.
    return postJson('/cart/add.js', { items: items }).then(function () { return true; }).catch(function (error) {
      if (error && error.status !== 422) throw error;
      var chain = Promise.resolve();
      items.forEach(function (item) {
        chain = chain.then(function () {
          return postJson('/cart/add.js', { items: [item] }).catch(function (itemError) {
            if (itemError && itemError.status === 422) {
              markOos(item.id);
              var rule = state.byHandle[item.properties._gwp];
              if (rule && rule.gift.mode === 'AUTO') {
                var next = firstAvailableVariant(rule, oosMap());
                if (next && next.id !== item.id) {
                  return postJson('/cart/add.js', { items: [{ id: next.id, quantity: item.quantity, properties: item.properties }] })
                    .catch(function (nextError) { if (nextError && nextError.status === 422) markOos(next.id); });
                }
              }
              return null;
            }
            throw itemError;
          });
        });
      });
      return chain.then(function () { return true; });
    });
  }

  function doReconcile(hint) {
    var cfg = loadConfig();
    if (!cfg) return Promise.resolve({ mutated: false, cart: hint || null, ev: evaluate(hint || null) });

    // Cheap pass on what we were handed: nothing to do -> no network at all.
    var pre = hint ? evaluate(hint) : null;
    if (pre && diffEmpty(pre.diff)) {
      state.last = { sig: pre.sig, noop: true };
      return Promise.resolve({ mutated: false, cart: hint, ev: pre });
    }
    if (pre && state.last.sig === pre.sig && state.last.noop) {
      return Promise.resolve({ mutated: false, cart: hint, ev: pre });
    }

    // Apply one diff; the caller re-evaluates the fresh cart and may run a
    // second pass (e.g. a gift that came back charged is removed straight
    // away rather than waiting for the next cart change).
    function applyDiff(cart, ev) {
      var chain = Promise.resolve();
      var removeKeys = Object.keys(ev.diff.remove);
      if (removeKeys.length) {
        removeKeys.forEach(function (key) { state.engineRemovals[key] = true; });
        chain = chain.then(function () { return postJson('/cart/update.js', { updates: ev.diff.remove }); });
      }
      if (ev.diff.add.length) {
        chain = chain.then(function () { return addWithFallback(ev.diff.add, cart); });
      }
      return chain.then(fetchCart);
    }

    // Server truth before touching anything.
    return fetchCart().then(function (cart) {
      var ev = evaluate(cart);
      if (diffEmpty(ev.diff)) {
        state.last = { sig: ev.sig, noop: true };
        return { mutated: false, cart: cart, ev: ev };
      }
      if (!rateAllows()) {
        state.last = { sig: ev.sig, noop: true };
        return { mutated: false, cart: cart, ev: ev, throttled: true };
      }
      rateStamp();

      return applyDiff(cart, ev)
        .then(function (fresh) {
          var after = evaluate(fresh);
          // Second pass only when the first one produced new work (a stalled
          // gift to take back out, a fallback variant to add). Never a third.
          if (!diffEmpty(after.diff) && rateAllows()) {
            rateStamp();
            return applyDiff(fresh, after).then(function (fresh2) { return evaluate(fresh2); });
          }
          return after;
        })
        .then(function (after) {
          state.last = { sig: after.sig, noop: true };
          dispatchUpdated(after.cart, after);
          return { mutated: true, cart: after.cart, ev: after };
        })
        .catch(function (error) {
          // Leave a breadcrumb but never throw into the drawer's render path.
          try { console.warn('[gwp] reconcile failed', error); } catch (e) { /* no-op */ }
          return fetchCart().then(function (fresh) {
            var after = evaluate(fresh);
            state.last = { sig: after.sig, noop: true }; // don't retry-storm
            return { mutated: false, cart: fresh, ev: after, error: error };
          });
        });
    }).catch(function (error) {
      // /cart.js itself failed (offline, blocked): stay quiet, try again on
      // the next cart change.
      return { mutated: false, cart: hint || null, ev: pre, error: error };
    });
  }

  function reconcile(hint) {
    if (state.inFlight) return state.inFlight;
    var run = enqueue(function () { return doReconcile(hint); });
    state.inFlight = run.finally(function () { state.inFlight = null; });
    return state.inFlight;
  }

  /* --------------------------- customer intents --------------------------- */

  function dismiss(handle) {
    var rule = state.byHandle[handle];
    if (!rule) return Promise.resolve({ mutated: false });
    var map = dismissed();
    map[handle] = true;
    setDismissed(map);
    // Remove now (evaluate() will also want it gone, but the customer just
    // clicked, so don't wait for the next settle).
    state.last = { sig: null, noop: false };
    return reconcile(null);
  }

  function choose(handle, variantId) {
    var rule = state.byHandle[handle];
    if (!rule) return Promise.resolve({ mutated: false });
    var variant = variantById(rule, variantId);
    if (!variant) return Promise.resolve({ mutated: false });
    var map = choices();
    map[handle] = variant.id;
    setChoices(map);
    var d = dismissed();
    if (d[handle]) { delete d[handle]; setDismissed(d); }
    state.last = { sig: null, noop: false };
    // Force the swap even if a different variant is present: evaluate() keeps
    // the present variant for CHOOSE, so we temporarily mark it for removal.
    return enqueue(function () {
      return fetchCart().then(function (cart) {
        var updates = {};
        (cart.items || []).forEach(function (item) {
          if (item.properties && String(item.properties._gwp) === handle && item.variant_id !== variant.id) {
            updates[item.key] = 0;
            state.engineRemovals[item.key] = true;
          }
        });
        if (!Object.keys(updates).length) return cart;
        return postJson('/cart/update.js', { updates: updates });
      });
    }).then(function () { return reconcile(null); });
  }

  /* -------------------------------- templates ----------------------------- */

  var GIFT_ICON = '<svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M3.5 9h13v7.5a1 1 0 0 1-1 1h-11a1 1 0 0 1-1-1V9Z" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/><path d="M2.75 6.25h14.5V9H2.75z" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/><path d="M10 6.25v11.25M10 6.25c-1.2-.1-3.4-.7-3.4-2.3 0-1.1.9-1.7 1.8-1.7 1.6 0 1.6 2.6 1.6 4Zm0 0c1.2-.1 3.4-.7 3.4-2.3 0-1.1-.9-1.7-1.8-1.7-1.6 0-1.6 2.6-1.6 4Z" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  function tilesTemplate(r) {
    var rule = r.rule;
    var oos = oosMap();
    var tiles = rule.gift.variants.map(function (variant) {
      var unavailable = !variant.available || !!oos[String(variant.id)];
      var selected = r.chosenVariantId === variant.id;
      var image = variant.image ? thumbUrl(variant.image, 160) : '';
      var name = variant.title + (variant.variantTitle ? ' – ' + variant.variantTitle : '');
      return '' +
        '<button type="button" class="cart-gift__tile' + (selected ? ' is-selected' : '') + (unavailable ? ' is-unavailable' : '') + '"' +
          ' data-gwp-choose="' + escapeHtml(rule.handle) + '" data-variant-id="' + variant.id + '"' +
          (unavailable ? ' disabled aria-disabled="true"' : '') + ' aria-pressed="' + (selected ? 'true' : 'false') + '">' +
          '<span class="cart-gift__tile-thumb">' + (image ? '<img src="' + escapeHtml(image) + '" alt="" width="64" height="64" loading="lazy">' : '') + '</span>' +
          '<span class="cart-gift__tile-name">' + escapeHtml(name) + '</span>' +
          '<span class="cart-gift__tile-price"><s>' + formatMoneyShort(variant.price * rule.gift.quantity) + '</s> <strong>' + (unavailable ? 'Sold out' : 'Free') + '</strong></span>' +
        '</button>';
    }).join('');
    return '<div class="cart-gift__tiles" role="group" aria-label="' + escapeHtml(r.messages.chooserHeading) + '">' + tiles + '</div>';
  }

  function groupTemplate(r) {
    var rule = r.rule;
    var showChooser = rule.gift.mode === 'CHOOSE' && (r.status === 'UNLOCKED_CHOOSE' || r.status === 'UNLOCKED_PRESENT' || r.status === 'UNLOCKED_ADD');
    var lines = r.present;
    var stateClass = r.stalled ? ' is-stalled' : ' is-unlocked';

    var rows = lines.map(function (item) {
      var image = item.image ? thumbUrl(item.image, 120) : '';
      var qty = item.quantity > 1 ? ' <span class="cart-gift__qty">x' + item.quantity + '</span>' : '';
      var charged = (item.final_line_price || 0) > 0;
      return '' +
        '<li class="cart-gift__item" data-key="' + escapeHtml(item.key) + '">' +
          '<span class="cart-gift__thumb">' + (image ? '<img src="' + escapeHtml(image) + '" alt="" width="56" height="56" loading="lazy">' : '') + '</span>' +
          '<a href="' + escapeHtml(item.url) + '" class="cart-gift__name">' + escapeHtml(item.product_title) + qty + '</a>' +
          '<span class="cart-gift__item-price">' +
            '<s>' + formatMoney(item.original_line_price) + '</s>' +
            '<strong>' + (charged ? formatMoney(item.final_line_price) : 'Free') + '</strong>' +
          '</span>' +
        '</li>';
    }).join('');

    var meta = r.stalled
      ? 'This gift is not discounting with the offers on this order, so it will be removed.'
      : (showChooser && !lines.length ? r.messages.chooserHeading : r.messages.unlocked);

    // Selection happens in the chooser modal, not inline: an unchosen rule
    // gets a primary CTA, a chosen one a quiet "change" control.
    var chooserControl = '';
    if (showChooser) {
      chooserControl = lines.length
        ? '<button type="button" class="cart-gift__change" data-gwp-open-chooser="' + escapeHtml(rule.handle) + '">' + escapeHtml(uiMessage('changeGift', 'Change gift')) + '</button>'
        : '<button type="button" class="cart-gift__choose-cta" data-gwp-open-chooser="' + escapeHtml(rule.handle) + '">' + escapeHtml(uiMessage('chooseCta', 'Choose your free gift')) + '</button>';
    }

    return '' +
      '<div class="cart-gift cart-gift--rule' + stateClass + (showChooser ? ' cart-gift--choose' : '') + '" data-gwp-rule="' + escapeHtml(rule.handle) + '">' +
        '<div class="cart-gift__head">' +
          '<span class="cart-gift__icon">' + GIFT_ICON + '</span>' +
          '<span class="cart-gift__info">' +
            '<span class="cart-gift__title"><span class="cart-gift__badge">' + escapeHtml(r.messages.badge) + '</span> ' + escapeHtml(rule.title) + '</span>' +
            (meta ? '<span class="cart-gift__meta">' + escapeHtml(meta) + '</span>' : '') +
          '</span>' +
          (lines.length ? '<span class="cart-gift__price"><s>' + formatMoneyShort(lines.reduce(function (s, i) { return s + (i.original_line_price || 0); }, 0)) + '</s><strong>' + (r.stalled ? 'Charged' : 'Free') + '</strong></span>' : '') +
        '</div>' +
        (rows ? '<ul class="cart-gift__list">' + rows + '</ul>' : '') +
        chooserControl +
        (lines.length && rule.gift.allowRemove ? '<button type="button" class="cart-gift__remove" data-gwp-dismiss="' + escapeHtml(rule.handle) + '">Remove gift</button>' : '') +
      '</div>';
  }

  /* ----------------------------- chooser modal ----------------------------- */
  // One modal for both surfaces: the drawer (geske-cart.js opens it) and the
  // /cart page (standalone bindings below). Selection always happens here; the
  // gift blocks only carry the buttons that open it. In 'checkout' context a
  // successful pick continues straight to /checkout, and skipping is never
  // remembered — the customer is simply asked again next time (deliberate).

  var CLOSE_ICON = '<svg width="14" height="14" viewBox="0 0 18 18" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.5"><line x1="3" y1="3" x2="15" y2="15"/><line x1="15" y1="3" x2="3" y2="15"/></svg>';

  var chooserUi = { root: null, context: null, opener: null, busy: false, open: false };

  function chooserRules(context, handles, ev) {
    if (context === 'checkout') return needsChoice(ev);
    return ev.rules.filter(function (r) {
      if (r.rule.gift.mode !== 'CHOOSE') return false;
      if (handles && handles.length && handles.indexOf(r.handle) === -1) return false;
      return r.status === 'UNLOCKED_CHOOSE' || r.status === 'UNLOCKED_PRESENT' || r.status === 'UNLOCKED_ADD';
    });
  }

  function chooserHtml(rules, context, notice) {
    var body;
    if (notice) {
      body = '<section class="gwp-modal__rule">' +
        '<h2 class="gwp-modal__heading" id="gwp-modal-heading">' + escapeHtml(notice.heading) + '</h2>' +
        '<p class="gwp-modal__notice">' + escapeHtml(notice.text) + '</p>' +
        '</section>';
    } else {
      body = rules.map(function (r, index) {
        return '<section class="gwp-modal__rule" data-gwp-modal-rule="' + escapeHtml(r.handle) + '">' +
          '<h2 class="gwp-modal__heading"' + (index === 0 ? ' id="gwp-modal-heading"' : '') + '>' + escapeHtml(r.messages.chooserHeading) + '</h2>' +
          tilesTemplate(r) +
          '</section>';
      }).join('');
    }
    var footer = context === 'checkout'
      ? '<div class="gwp-modal__footer"><button type="button" class="gwp-modal__skip" data-gwp-modal-skip>' + escapeHtml(uiMessage('checkoutWithout', 'Checkout without gift')) + '</button></div>'
      : '';
    return '<div class="gwp-modal__backdrop" data-gwp-modal-close></div>' +
      '<div class="gwp-modal__panel" role="dialog" aria-modal="true" aria-labelledby="gwp-modal-heading" tabindex="-1">' +
        '<button type="button" class="gwp-modal__close" data-gwp-modal-close aria-label="Close">' + CLOSE_ICON + '</button>' +
        '<div class="gwp-modal__body">' + body + '</div>' +
        footer +
      '</div>';
  }

  function ensureChooserRoot() {
    if (chooserUi.root) return chooserUi.root;
    var root = document.createElement('div');
    root.className = 'gwp-modal';
    root.hidden = true;
    root.addEventListener('click', onChooserClick);
    root.addEventListener('keydown', onChooserKeydown);
    document.body.appendChild(root);
    chooserUi.root = root;
    return root;
  }

  // Coming back from checkout with the browser back button restores the page
  // from the bfcache with the modal still painted (and the skip button
  // disabled): close it so the cart is usable again.
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) closeChooser();
  });

  function focusChooser() {
    if (!chooserUi.root) return;
    var target = chooserUi.root.querySelector('[data-gwp-choose]:not([disabled])') ||
      chooserUi.root.querySelector('.gwp-modal__close');
    if (target && target.focus) target.focus();
  }

  function openChooser(opts) {
    opts = opts || {};
    var context = opts.context === 'checkout' ? 'checkout' : 'browse';
    var ev = state.lastEval;
    if (!ev) return false;
    var rules = chooserRules(context, opts.handles, ev);
    if (!rules.length) return false;

    var root = ensureChooserRoot();
    chooserUi.context = context;
    chooserUi.opener = document.activeElement || null;
    chooserUi.open = true;
    chooserUi.busy = false;
    root.innerHTML = chooserHtml(rules, context, null);
    root.hidden = false;
    // The drawer already pins the body; only lock scroll when it hasn't.
    if (!document.body.classList.contains('drawer-open')) document.body.classList.add('gwp-modal-open');
    focusChooser();
    return true;
  }

  function closeChooser() {
    if (!chooserUi.root || !chooserUi.open) return;
    chooserUi.open = false;
    chooserUi.context = null;
    chooserUi.busy = false;
    chooserUi.root.hidden = true;
    chooserUi.root.innerHTML = '';
    document.body.classList.remove('gwp-modal-open');
    if (chooserUi.opener && chooserUi.opener.focus) {
      try { chooserUi.opener.focus(); } catch (error) { /* no-op */ }
    }
    chooserUi.opener = null;
  }

  function setChooserBusy(busy) {
    chooserUi.busy = busy;
    if (!chooserUi.root) return;
    var panel = chooserUi.root.querySelector('.gwp-modal__panel');
    if (panel) panel.classList.toggle('is-busy', busy);
  }

  function renderChooserRules(rules) {
    if (!chooserUi.root || !chooserUi.open) return;
    chooserUi.root.innerHTML = chooserHtml(rules, chooserUi.context, null);
    focusChooser();
  }

  function renderChooserNotice(r) {
    if (!chooserUi.root || !chooserUi.open) return;
    chooserUi.root.innerHTML = chooserHtml([], chooserUi.context, {
      heading: r.messages.chooserHeading,
      text: "This gift is not discounting with the offers on this order, so it can't be added. You can checkout without it."
    });
    focusChooser();
  }

  function onChooserKeydown(event) {
    if (!chooserUi.open) return;
    if (event.key === 'Escape') {
      // Stop here so the drawer's own Escape handler doesn't also close the drawer.
      event.preventDefault();
      event.stopPropagation();
      closeChooser();
      return;
    }
    if (event.key !== 'Tab') return;
    var items = chooserUi.root.querySelectorAll('button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])');
    if (!items.length) return;
    var first = items[0];
    var last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function onChooserClick(event) {
    var closeBtn = event.target.closest('[data-gwp-modal-close]');
    if (closeBtn) {
      event.preventDefault();
      closeChooser();
      return;
    }

    var skip = event.target.closest('[data-gwp-modal-skip]');
    if (skip) {
      event.preventDefault();
      skip.disabled = true;
      window.location.href = '/checkout';
      return;
    }

    var tile = event.target.closest('[data-gwp-choose]');
    if (!tile) return;
    // The drawer and the standalone bindings also delegate [data-gwp-choose]
    // on document; the modal owns its own tiles.
    event.preventDefault();
    event.stopPropagation();
    if (chooserUi.busy || tile.disabled) return;

    var handle = tile.getAttribute('data-gwp-choose');
    var variantId = tile.getAttribute('data-variant-id');
    var context = chooserUi.context;
    setChooserBusy(true);

    choose(handle, variantId).then(function (result) {
      var after = (result && result.ev) || null;
      if (after) paintPanel(after); // /cart page panel + nudge; no-op in the drawer
      // Escape/backdrop while the pick was in flight: the gift still landed,
      // but the customer backed out — never navigate under them.
      if (!chooserUi.open) return;
      if (context !== 'checkout') {
        closeChooser();
        return;
      }
      var chosen = after ? after.rules.filter(function (r) { return r.handle === handle; })[0] : null;
      if (chosen && chosen.stalled) {
        // Shopify won't zero this gift against the current offers; the engine
        // already took the line back out. Explain instead of navigating.
        setChooserBusy(false);
        renderChooserNotice(chosen);
        return;
      }
      var remaining = after ? needsChoice(after) : [];
      if (remaining.length) {
        setChooserBusy(false);
        renderChooserRules(remaining);
        return;
      }
      window.location.href = '/checkout';
    }).catch(function () {
      closeChooser();
    });
  }

  /* ---------------------------- standalone (/cart) ------------------------- */
  // On the cart page geske-cart.js is not loaded, so the engine keeps the cart
  // honest itself and paints a small panel under the line items.

  function panelTemplate(ev) {
    var parts = [];
    if (ev.next) {
      parts.push('<div class="cart-gift cart-gift--progress"><div class="cart-gift__head"><span class="cart-gift__icon">' + GIFT_ICON + '</span><span class="cart-gift__info"><span class="cart-gift__title">' + escapeHtml(ev.next.messages.pending) + '</span></span></div><div class="cart-gift__track"><div class="cart-gift__fill" style="width:' + ev.next.progress + '%"></div></div></div>');
    }
    ev.rules.forEach(function (r) {
      if (r.winner && !r.dismissed && (r.present.length || r.mode === 'CHOOSE')) parts.push(groupTemplate(r));
    });
    return parts.join('');
  }

  var panelObserver = null;
  function paintPanel(ev) {
    var panels = document.querySelectorAll('[data-gwp-panel]');
    var nudges = document.querySelectorAll('[data-gwp-checkout-nudge]');
    if (!panels.length && !nudges.length) return;
    var html = panels.length ? panelTemplate(ev) : '';
    Array.prototype.forEach.call(panels, function (panel) {
      if (panel.getAttribute('data-gwp-painted') === ev.sig && panel.innerHTML) return;
      panel.innerHTML = html;
      panel.hidden = !html;
      panel.setAttribute('data-gwp-painted', ev.sig);
    });
    // A short line next to the checkout button so the chooser interrupt is
    // never a surprise: painted from the same evaluation as the panel.
    var pending = needsChoice(ev);
    Array.prototype.forEach.call(nudges, function (nudge) {
      if (pending.length) {
        nudge.textContent = pending[0].messages.chooserHeading;
        nudge.hidden = false;
      } else {
        nudge.textContent = '';
        nudge.hidden = true;
      }
      nudge.setAttribute('data-gwp-painted', ev.sig);
    });
  }

  function standaloneSettle() {
    return reconcile(null).then(function (result) {
      var ev = result.ev || (result.cart ? evaluate(result.cart) : null);
      if (ev) paintPanel(ev);
      return result;
    });
  }

  var standaloneTimer = null;
  function standaloneInit() {
    if (!loadConfig()) return;
    // The drawer owns reconciliation wherever it is loaded.
    if (document.querySelector('[data-cart-drawer]')) return;

    var kick = function () {
      if (standaloneTimer) window.clearTimeout(standaloneTimer);
      standaloneTimer = window.setTimeout(function () { standaloneTimer = null; standaloneSettle(); }, 120);
    };

    document.addEventListener('cart:update', function (event) {
      if (event.detail && event.detail.data && event.detail.data.source === 'gwp-engine') return;
      kick();
    });
    document.addEventListener('discount:update', kick);
    window.addEventListener('pageshow', function (event) { if (event.persisted) kick(); });

    document.addEventListener('click', function (event) {
      var dismissBtn = event.target.closest('[data-gwp-dismiss]');
      if (dismissBtn) {
        event.preventDefault();
        dismissBtn.disabled = true;
        dismiss(dismissBtn.getAttribute('data-gwp-dismiss')).then(function (result) {
          if (result && result.ev) paintPanel(result.ev);
        });
        return;
      }
      var openBtn = event.target.closest('[data-gwp-open-chooser]');
      if (openBtn) {
        event.preventDefault();
        openChooser({ handles: [openBtn.getAttribute('data-gwp-open-chooser')], context: 'browse' });
        return;
      }
      var chooseBtn = event.target.closest('[data-gwp-choose]');
      if (chooseBtn) {
        event.preventDefault();
        choose(chooseBtn.getAttribute('data-gwp-choose'), chooseBtn.getAttribute('data-variant-id')).then(function (result) {
          if (result && result.ev) paintPanel(result.ev);
        });
      }
    });

    // The /cart page checkout button (snippets/cart-summary.liquid). An
    // unlocked CHOOSE rule with nothing picked interrupts once with the
    // chooser; skipping goes straight to checkout and is never remembered.
    // Accelerated checkout buttons (Shop Pay etc.) cannot be intercepted —
    // the nudge line painted next to the buttons is the mitigation.
    document.addEventListener('click', function (event) {
      var btn = event.target.closest('#checkout, button[name="checkout"]');
      if (!btn) return;
      var ev = state.lastEval;
      if (!ev || !needsChoice(ev).length) return;
      event.preventDefault();
      openChooser({ context: 'checkout' });
    });

    // morphSection replaces the cart-items subtree (and our panel's content
    // with the server-rendered empty div), so repaint after every morph.
    try {
      var host = document.querySelector('cart-items-component') || document.body;
      panelObserver = new MutationObserver(function () {
        if (!state.lastEval) return;
        var stale = function (el) { return el && el.getAttribute('data-gwp-painted') !== state.lastEval.sig; };
        if (stale(document.querySelector('[data-gwp-panel]')) || stale(document.querySelector('[data-gwp-checkout-nudge]'))) {
          paintPanel(state.lastEval);
        }
      });
      panelObserver.observe(host, { childList: true, subtree: true });
    } catch (error) { /* no-op */ }

    kick();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', standaloneInit);
  } else {
    standaloneInit();
  }

  /* --------------------------------- export ------------------------------- */

  window.GeskeGwp = {
    config: config,
    isRuleHandle: isRuleHandle,
    evaluate: evaluate,
    reconcile: reconcile,
    dismiss: dismiss,
    choose: choose,
    needsChoice: needsChoice,
    chooser: { open: openChooser, close: closeChooser },
    enqueue: enqueue,
    formatMoney: formatMoney,
    formatMoneyShort: formatMoneyShort,
    fillTemplate: fillTemplate,
    templates: { group: groupTemplate, tiles: tilesTemplate, panel: panelTemplate },
    state: function () { return state.lastEval; },
    wasEngineRemoval: function (key) { return !!state.engineRemovals[key]; }
  };
})();
